'use client';

/**
 * The Datadog RUM + Logs bootstrap, and the named setters for the global-context
 * dimensions this app owns.
 *
 * Two things live here and they are governed differently.
 *
 * **The bootstrap is frozen.** `initDatadog`'s config — the `proxy`,
 * `sessionSampleRate`, `sessionReplaySampleRate`, `allowFallbackToLocalStorage`,
 * both `beforeSend` policies, the noise counters, the `dev-local` guard — each
 * encodes an incident. Change it only under a ticket about the SDK lifecycle,
 * with the incident named.
 *
 * **The dimensions are a table.** Every setter below delegates to the one
 * `setGlobalDimension` primitive; which sinks carry a key, whether the on-prem
 * gate applies and what a `null` means are rows in `GLOBAL_DIMENSIONS`
 * (`@/utils/telemetry/global-dimensions`), not comments here. What stays here is
 * the prose: why a dimension exists, who calls it, what a dashboard does with
 * it. Rules: `.ai-context/telemetry-dimensions.md`.
 */

import { type NoteEditorVariant } from '@/models/analytics/features/notes';
import { isTauri } from '@/tauri/utils';
import { getDesktopInstallerType } from '@/utils/appConfiguration';
import { getDesktopBuildId } from '@/utils/appVersion';
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import { DATADOG_SERVICE_DESKTOP, DATADOG_SERVICE_WEB } from '@/utils/datadogServiceName';
import { getDeviceId } from '@/utils/http/tracing/deviceId';
import { getDeviceSessionId } from '@/utils/http/tracing/deviceSessionId';
import { interceptConsoleMethods } from '@/utils/logger/consoleInterceptor';
import {
  redactRumEventUrls,
  redactSensitiveUrlParams,
} from '@/utils/redact-tracing-urls';
import { formatVersion } from '@/utils/releaseVersion';
import { admitRumError, buildRumErrorSignature } from '@/utils/rumErrorBurstThrottle';
import { setDatadogEditorVariantStore } from '@/utils/telemetry/editor-context';
import {
  type GlobalDimensionValue,
  setGlobalDimension,
} from '@/utils/telemetry/global-dimensions';
import { isSessionReplayBlockedRoute } from '@/utils/telemetry/replay-blocked-routes';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { registerAnalyticsAdapter } from '@heidi/runtime/analytics';
import { admitLogLine, buildLogSignature } from '@heidi/runtime/logger';
import {
  type EHRProvider,
  type Region,
  type SubscriptionTier,
} from '@heidi/schema/ml-scribe';

// P2 [3/5]: Datadog RUM transport binding for the @heidi/runtime/analytics
// pipeline — registered at this SDK-owning site, a suppressed build registers
// nothing (F5: absence). The pipeline routes Datadog on EVERY event (always-on
// destination); pre-init addAction calls no-op inside the SDK exactly as the
// legacy effect's unconditional calls did. TELEMETRY-GATE.
if (typeof window !== 'undefined' && isSaasTelemetryEnabled) {
  registerAnalyticsAdapter({
    destination: 'datadog',
    enabled: true,
    send: (payload) => {
      datadogRum.addAction(payload.event, payload.props);
    },
  });
}

let rumInitialized = false;
let logsInitialized = false;

export type DatadogClientStatus = {
  configured: boolean;
  rumInitialized: boolean;
  logsInitialized: boolean;
};

const isDatadogConfigured = () =>
  isSaasTelemetryEnabled &&
  Boolean(process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN) &&
  formatVersion(
    process.env.NEXT_PUBLIC_RELEASE_REF,
    process.env.NEXT_PUBLIC_RELEASE_SHA
  ) !== 'dev-local';

/**
 * Returns local SDK state only. Browser intake acknowledgement is not exposed
 * by the Datadog SDK, so this must not be treated as proof of delivery.
 */
export function getDatadogClientStatus(): DatadogClientStatus {
  return {
    configured: isDatadogConfigured(),
    rumInitialized,
    logsInitialized,
  };
}

/**
 * Queues a bounded action for the diagnostics report. A true result means the
 * SDK accepted it locally, not that Datadog intake received it.
 */
export function enqueueDatadogClientDiagnosticsAction(): boolean {
  if (!rumInitialized) return false;

  try {
    datadogRum.addAction('client_sdk_self_report', {});
    return true;
  } catch {
    return false;
  }
}

export function addDatadogRumAction(
  actionName: string,
  context?: Parameters<typeof datadogRum.addAction>[1]
): void {
  // TELEMETRY-GATE: shared feature code emits through this SaaS-only boundary.
  if (!isSaasTelemetryEnabled) return;
  datadogRum.addAction(actionName, context);
}

export function addDatadogRumError(
  error: unknown,
  context?: Parameters<typeof datadogRum.addError>[1]
): void {
  // TELEMETRY-GATE: shared feature code emits through this SaaS-only boundary.
  if (!isSaasTelemetryEnabled) return;
  datadogRum.addError(error, context);
}

// Benign noise dropped from both Datadog pipelines: the logs SDK forwards
// uncaught errors on its own (forwardErrorsToLogs defaults to true).
// Matched with `includes`, not `startsWith`: uncaught errors reach the SDKs
// wrapped, e.g. `Uncaught "ResizeObserver loop completed with undelivered
// notifications."`.
type IgnoredErrorKind =
  | 'resize_observer_loop'
  | 'extension_circular_json'
  | 'audio_graph_logout'
  | 'abort_route_change'
  | 'abort_route_user_confirmation'
  | 'browser_extension';

// RUM-only drop classes join the counter alongside the message-matched kinds.
type DroppedNoiseKind = IgnoredErrorKind | 'desktop_csp_violation';

// The RUM SDK renders a CSP violation report as
//   message: csp_violation: '<blocked>' blocked by '<directive>' directive
//   stack:   <directive>: <message>\n  at <anonymous> @ <source>:<line>:<col>
// where <blocked> is the blocked resource (the literal 'inline' for an inline
// script) and <source> is the file that triggered the violation.
const CSP_VIOLATION_MESSAGE_PREFIX = 'csp_violation:';

const parseCspBlockedResourceAndDirective = (
  message: string
): { blockedResource: string | null; violatedDirective: string | null } => {
  const parsed = /^csp_violation: '(.+?)' blocked by '([^']*)' directive/.exec(message);
  return {
    blockedResource: parsed?.[1] ?? null,
    violatedDirective: parsed?.[2] ?? null,
  };
};

const parseCspTriggeringSource = (stack: string | undefined): string | null => {
  const source = stack ? /@ (\S+)/.exec(stack)?.[1] : undefined;
  // The frame carries a trailing :line:column that is not part of the URL.
  return source ? source.replace(/:\d+:\d+$/, '') : null;
};

// Downgrades one desktop CSP violation report to exactly one warn-level log
// line, keeping the report queryable in Datadog Logs
// (service:scribe-desktop-app status:warn "desktop CSP violation report")
// after the RUM error is dropped. Guarded end to end: it must never throw
// inside beforeSend, and it is a no-op when the logs SDK is not there to take
// the line. (Pre-init is fine: the logs SDK queues logger calls made before
// datadogLogs.init and flushes them at init.)
const CSP_VIOLATION_LOG_MESSAGE = 'desktop CSP violation report';

const logDesktopCspViolation = (
  message: string,
  stack: string | undefined,
  event: { view?: { name?: string }; session?: { id?: string } }
): void => {
  try {
    if (typeof datadogLogs?.logger?.warn !== 'function') return;
    const { blockedResource, violatedDirective } =
      parseCspBlockedResourceAndDirective(message);

    // Both URLs are redacted before they reach the line. A blocked or
    // triggering URI can be first-party and carry a bearer token or a patient
    // identifier in its query or hash, and the logs beforeSend only redacts
    // the view URL and referrer, never custom attributes: whatever is put in
    // these attributes is what ships.
    const redactedBlockedResource =
      blockedResource === null ? null : redactSensitiveUrlParams(blockedResource);
    const parsedSource = parseCspTriggeringSource(stack);
    // 'inline' is a real blocked-resource value, not an unknown-source
    // placeholder. Emitting it when no frame parsed would conflate a genuine
    // inline script with a source we could not read, and quietly poison a
    // `@triggering_source:inline` query; null says "unknown" instead.
    const triggeringSource =
      parsedSource === null
        ? blockedResource === 'inline'
          ? 'inline'
          : null
        : redactSensitiveUrlParams(parsedSource);

    // One repeating violation (a blocked resource retried in a loop) must not
    // emit an unbounded number of warn lines: this call goes straight to the
    // Datadog Logs SDK and so bypasses the app logger's per-signature cap.
    // Reuse that same cap here, keyed on the blocked resource + directive, so
    // the leading occurrences stay diagnosable and only the tail is dropped.
    // The RUM drop and the noise count are the caller's and happen either way.
    const signature = buildLogSignature('warn', CSP_VIOLATION_LOG_MESSAGE, {
      blocked_resource: redactedBlockedResource,
      violated_directive: violatedDirective,
    });
    if (!admitLogLine(signature, Date.now()).keep) return;

    datadogLogs.logger.warn(CSP_VIOLATION_LOG_MESSAGE, {
      blocked_resource: redactedBlockedResource,
      violated_directive: violatedDirective,
      triggering_source: triggeringSource,
      view_name: event.view?.name ?? null,
      rum_session_id: event.session?.id ?? null,
    });
  } catch {
    // A diagnostic log must never break beforeSend.
  }
};

// Extension messaging failures that arrive with no stack frame to attribute.
const BROWSER_EXTENSION_MESSAGE = 'Failed to connect to MetaMask';
// Also what our own Heidi-extension handoff rejects with, so it is dropped
// only when uncaught; a logged or handled report of it stays visible.
const UNCAUGHT_BROWSER_EXTENSION_MESSAGE =
  'Could not establish connection. Receiving end does not exist.';

// The SDKs format every browser's stack as `  at <fn> @ <url>:<line>:<col>`.
const EXTENSION_FRAME = /\b(?:chrome|moz|safari|safari-web)-extension:\/\//;

const isTopFrameInExtension = (stack: string): boolean => {
  const topFrame = stack.split('\n').find((line) => /^\s+at\s/.test(line));
  return topFrame !== undefined && EXTENSION_FRAME.test(topFrame);
};

// `uncaughtStack` is passed only for uncaught errors: a handled report whose
// stack happens to start in an extension (our fetch wrapped by one) is ours.
const getIgnoredErrorKind = (
  message: string,
  uncaughtStack?: string
): IgnoredErrorKind | null => {
  if (message.includes('ResizeObserver loop')) {
    return 'resize_observer_loop';
  }
  // Next.js Pages Router throws this after emitting routeChangeError when a
  // transition is cancelled (e.g. template-builder back navigation).
  if (message.includes('Route change aborted.')) {
    return 'abort_route_change';
  }
  // Browser extensions JSON.stringify our React DOM nodes (circular refs).
  if (
    message.includes('Converting circular structure to JSON') &&
    message.includes('__reactFiber')
  ) {
    return 'extension_circular_json';
  }
  // Logout tears down the capture audio graph by aborting with this reason.
  if (message.includes('Audio graph shut down for logout')) {
    return 'audio_graph_logout';
  }
  // Next.js Pages Router unsaved-changes guards throw this to cancel navigation.
  if (message.includes("Abort route change by user's confirmation.")) {
    return 'abort_route_user_confirmation';
  }
  if (
    message.includes(BROWSER_EXTENSION_MESSAGE) ||
    (uncaughtStack !== undefined &&
      (message.includes(UNCAUGHT_BROWSER_EXTENSION_MESSAGE) ||
        isTopFrameInExtension(uncaughtStack)))
  ) {
    return 'browser_extension';
  }
  return null;
};

export function initDatadog() {
  // TELEMETRY-GATE: no Datadog RUM/Logs, no sys.heidihealth.com reverse-proxy egress.
  if (!isSaasTelemetryEnabled) return;
  if (!process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN) return;

  const version = formatVersion(
    process.env.NEXT_PUBLIC_RELEASE_REF,
    process.env.NEXT_PUBLIC_RELEASE_SHA
  );
  // Un-built local dev (`pnpm dev`) injects no release ref/SHA, so
  // formatVersion() returns the literal 'dev-local'. Initializing RUM/Logs
  // here would emit telemetry tagged with whatever NEXT_PUBLIC_ENVIRONMENT the
  // dev's .env points at (commonly `staging`) under version `dev-local` from
  // localhost — polluting the per-version canary cohorts and making Datadog
  // Change Tracking flag the non-semver version as a spurious "rollback"
  // (ARC-529). Deployed builds always inject a real version (Vercel:
  // VERCEL_GIT_COMMIT_*; desktop GHA: GITHUB_* — see next.config.js), so this
  // only skips local dev, never desktop or a deployed web build.
  if (version === 'dev-local') return;

  const env = process.env.NEXT_PUBLIC_ENVIRONMENT || 'production';
  const serviceName = isTauri() ? DATADOG_SERVICE_DESKTOP : DATADOG_SERVICE_WEB;
  const datadogSite = process.env.NEXT_PUBLIC_DATADOG_SITE || 'datadoghq.com';

  const isProduction = getEnvironment() === ENV_NAME.PROD;
  // E2E asserts on RUM telemetry; staging's 50% session sampling would make
  // those assertions flaky, so force full sampling under the e2e harness flag.
  const isE2E = typeof window !== 'undefined' && window.__IS_E2E__ === true;
  // `redactRumEventUrls` scrubs the onboarding page's token from RUM events,
  // but replay's first snapshot records `location.href` outside beforeSend.
  const isReplayBlockedHere =
    typeof window !== 'undefined' &&
    isSessionReplayBlockedRoute(window.location.pathname);
  // Datadog RUM replay is the single session recorder on desktop (Amplitude
  // and PostHog replay are disabled on Tauri — see amplitude-provider.tsx
  // and posthog-provider.tsx).
  // RUM metrics (page load, long tasks, errors) are unaffected by this rate —
  // they ride on `sessionSampleRate`, not this one.
  const productionReplaySampleRate = isProduction ? 1 : 0;

  const baseConfig = {
    applicationId: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID || '',
    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
    site: datadogSite,
    proxy: (options: { path: string; parameters: string }) => {
      // cloudfront reverse proxy sys.heidihealth.com configuration for datadog can be found in
      // https://github.com/oscerai/infra/blob/main/shared_infra/prod/global/cloudfront.tf
      if (datadogSite === 'datadoghq.com') {
        return `https://sys.heidihealth.com${options.path}?${options.parameters}`;
      }

      const region = datadogSite === 'datadoghq.eu' ? 'eu' : datadogSite.split('.', 1)[0]; // e.g., 'us3' from 'us3.datadoghq.com'
      return `https://sys.heidihealth.com/rum/${region}${options.path}?${options.parameters}`;
    },
    sessionSampleRate: isProduction || isE2E ? 100 : 50,
    service: serviceName,
    env: env,
    version: version,
    tags: [`env:${env}`, `app:${serviceName}`],
    // macOS WKWebView rejects cookie writes on the `https://tauri.localhost`
    // origin, so the DD SDK finds no session store and silently drops ALL RUM
    // events. localStorage works there; on Windows/web cookies succeed and this
    // is never reached. Must be identical on RUM and Logs so they share `_dd_s`
    // and correlate on the same session_id (Datadog docs).
    allowFallbackToLocalStorage: true,
  };

  // Sampled per-kind drop counts keep the loop signal trendable after the
  // filter hides it. Counts are a lower bound: nothing flushes after the
  // last reporting threshold. Declared before both inits because the RUM
  // beforeSend now counts its own drop class and can run synchronously
  // inside datadogRum.init (initial view assembly) — a `const` declared
  // between the inits would still be in its temporal dead zone there. A
  // count emitted before datadogLogs.init is safe: the logs SDK queues
  // pre-init logger calls and flushes them at init.
  const droppedNoiseCounts = new Map<DroppedNoiseKind, number>();
  const reportsDroppedNoise = Math.random() < 0.01;
  const countDroppedNoise = (kind: DroppedNoiseKind) => {
    const count = (droppedNoiseCounts.get(kind) ?? 0) + 1;
    droppedNoiseCounts.set(kind, count);
    if (reportsDroppedNoise && (count === 1 || count % 100 === 0)) {
      datadogLogs.logger.info('Known-noise error dropped', {
        noise_kind: kind,
        drop_count: count,
      });
    }
  };

  // Initialize RUM
  datadogRum.init({
    ...baseConfig,
    sessionReplaySampleRate: isReplayBlockedHere ? 0 : productionReplaySampleRate,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    enablePrivacyForActionName: true,
    allowedTracingUrls: [
      (url) => url.includes('heidihealth.com') && !url.includes('sys.heidihealth.com'),
    ],
    defaultPrivacyLevel: 'mask',
    beforeSend: (event) => {
      // Strip credential values from URL query strings before the event is sent.
      redactRumEventUrls(event);

      if (event.type === 'error' && typeof event.error?.message === 'string') {
        const { message } = event.error;

        // Not counted here: the logs beforeSend owns these kinds' drop counter
        // (uncaught errors reach both pipelines; counting both would double).
        const uncaughtStack =
          event.error.handling === 'unhandled' && typeof event.error.stack === 'string'
            ? event.error.stack
            : undefined;
        if (getIgnoredErrorKind(message, uncaughtStack)) {
          return false;
        }

        // On desktop, a CSP violation report never becomes a RUM error. CSP
        // violation errors do not affect users — the browser blocks the tag
        // and the app carries on — but they pollute error tracking, burying
        // the real errors, inflating sessions-with-errors and costing RUM
        // ingestion. Each report is downgraded to exactly one warn-level log
        // (service:scribe-desktop-app status:warn "desktop CSP violation
        // report"), so a CSP regression still shows as a spike in warn logs.
        // Counted HERE: CSP reports reach only this pipeline (forwardReports
        // is off on the logs SDK), so there is no double count.
        if (
          isTauri() &&
          event.error.source === 'report' &&
          message.startsWith(CSP_VIOLATION_MESSAGE_PREFIX)
        ) {
          logDesktopCspViolation(
            message,
            typeof event.error.stack === 'string' ? event.error.stack : undefined,
            event
          );
          countDroppedNoise('desktop_csp_violation');
          return false;
        }

        // The tiptap view-proxy throw is otherwise an untyped
        // `source:source` error. Stamp a class so the post-destroy
        // regression is queryable without matching the full message.
        if (message.includes('The editor view is not available')) {
          event.context = {
            ...event.context,
            error_class: 'tiptap_view_unavailable',
          };
        }

        // The vendored Hero Health (EMIS) widget throws its own
        // `ApiError` on a 401 from an uncaught async path. The app already
        // catches this at the window level (use-hero-auth-error-recovery.ts)
        // to suppress the console warning and silently re-auth the widget —
        // but that listener's event.preventDefault() only cancels the
        // browser's default action; it does not stop this SDK's own,
        // independent unhandledrejection/error collector from still filing
        // the same error as "unhandled". Drop it here instead, scoped to:
        // - `handling === 'unhandled'` so deliberate `datadogRum.addError`
        //   reports (e.g. hero-health-error-boundary.tsx) still get through;
        // - the vendor's error shape (`ApiError` + an unauthori[sz]ed
        //   message — matched loosely, since the window listener keys on
        //   `status === 401` and the exact message wording is the vendor's);
        // - a stack referencing the widget's dedicated `hero-health-vendor`
        //   chunk (named in next.config.js), so unrelated app 401s still
        //   report.
        if (
          event.error.type === 'ApiError' &&
          event.error.handling === 'unhandled' &&
          /unauthori[sz]ed/i.test(message) &&
          typeof event.error.stack === 'string' &&
          event.error.stack.includes('hero-health')
        ) {
          return false;
        }

        // Cap what one looping tab can emit of a single error. Runs after the
        // known-noise drops so those never consume a signature's budget.
        // Deliberately covers handled addError reports too — a loop calling
        // addError is still a loop.
        const verdict = admitRumError(buildRumErrorSignature(event.error), Date.now());
        if (!verdict.keep) return false;
        if (verdict.droppedInPreviousWindow > 0) {
          event.context = {
            ...event.context,
            rum_error_burst_dropped: verdict.droppedInPreviousWindow,
          };
        }
      }
      return true;
    },
  });
  rumInitialized = true;

  datadogLogs.init({
    ...baseConfig,
    // This pipeline sees every uncaught error exactly once (no session
    // sampling, no burst cap), so the drop counter lives here. Scoped to
    // error events so deliberate logger.* lines mentioning a noise string
    // are never swallowed or counted.
    beforeSend: (event) => {
      // Every log line carries the page URL and referrer; /auth/onboard
      // arrives with Auth0's transaction `?state=`, so run the RUM redactor
      // over the view fields before anything else looks at the event.
      redactRumEventUrls({ view: event.view });

      if (event.status === 'error' && typeof event.message === 'string') {
        // Forwarded uncaught errors carry origin 'source'; logger.error lines
        // carry 'logger' and are deliberate reports.
        const uncaughtStack =
          event.origin === 'source' && typeof event.error?.stack === 'string'
            ? event.error.stack
            : undefined;
        const kind = getIgnoredErrorKind(event.message, uncaughtStack);
        if (kind) {
          countDroppedNoise(kind);
          return false;
        }
      }
      return true;
    },
    // forwardErrorsToLogs: true,
    // forwardConsoleLogs: ['error', 'warn', 'info', 'debug'],
    // forwardReports: 'all',
  });
  logsInitialized = true;
  interceptConsoleMethods();

  // Device/build dimension as Datadog global context — attaches to all
  // subsequent RUM events + logs for cross-event filtering (parity with
  // distribution_channel/mdm_provider in install-source-context.ts and
  // desktop_version in _app.tsx). Event-scoped global attribute, NOT user
  // identity — `setUser` deliberately stays id-only.
  setGlobalDimension('installer_type', getDesktopInstallerType());

  // Tag every RUM event and log line with the same device_id / device_session_id
  // that go out on the X-Device-Id / X-Device-Session-Id request headers
  // (DEV-11834). Lets us join FE-emitted Datadog events to BE-emitted ones for
  // the same device + session — without these, `@device_session_id:<id>`
  // queries would only find BE logs and miss the matching FE RUM / browser-log
  // rows that share the session.
  setGlobalDimension('device_id', getDeviceId());
  setGlobalDimension('device_session_id', getDeviceSessionId());

  // APP-6116 (§5.11): tag every RUM event and log line with the baked CI build
  // SHA as global context, so build provenance is queryable across ALL Datadog
  // telemetry (views, errors, logs) — not just the per-event RUM actions that
  // already carry it via `extendedProps`. Desktop-only: getDesktopBuildId() is
  // undefined on web. `region` / `update_channel` are runtime (post-login) and
  // ride on the per-event RUM action props rather than global context, which is
  // set once here at init before the authenticated region resolves.
  // The `if` stays a caller-side `if`. An absent build_id is never written;
  // passing `buildId ?? null` would issue a remove instead (registry rule 5).
  const buildId = getDesktopBuildId();
  if (buildId) {
    setGlobalDimension('build_id', buildId);
  }

  // Rolling releases (canary playbook P0): pin every RUM event / log line
  // to the exact Vercel deployment that built this bundle. `version` stays
  // the human-readable release axis (sourcemap uploads are keyed to it);
  // deployment_id is the machine join key — rollback targets, same-SHA
  // redeploys, and the per-project builds of a single release. Unset on
  // desktop (GHA), local, and on-prem builds.
  const deploymentId = process.env.NEXT_PUBLIC_VERCEL_DEPLOYMENT_ID;
  if (deploymentId) {
    setGlobalDimension('deployment_id', deploymentId);
  }
}

export function setUser({ userId }: { userId: string }) {
  // installer_type is a device/build dimension, not user identity — it rides as a
  // per-event property via trackEventV2, not on the Datadog user.
  datadogRum.setUser({ id: userId });
  datadogLogs.setUser({ id: userId });
}

export function setUserProperty({ id, value }: { id: string; value: string }) {
  datadogRum.setUserProperty(id, value);
}

/**
 * Re-attach the desktop `update_channel` as Datadog **global** context once the
 * authenticated region resolves (APP-6116). `build_id` is set at init (it's
 * static/baked), but `update_channel` is post-login. Without this, auto-collected
 * RUM errors and native crashes — the events that DON'T flow through
 * `trackEventV2`'s per-event `extendedProps` — would be filterable by `build_id`
 * but not `update_channel`, so "show all uk-channel crashes" would silently miss
 * exactly the crash/error events deployment telemetry exists to triage. Called
 * from OpenFeatureContextSync's region-resolved effect (alongside the Snowplow
 * setter). On-prem-gated to match `initDatadog`; a no-op there since RUM/Logs
 * never initialize.
 */
export function setDatadogUpdateChannel(updateChannel: string | null) {
  setGlobalDimension('update_channel', updateChannel);
}

/**
 * Tag RUM and Logs with the authenticated data-residency region, so the client
 * session-flow metric slices by regional stack (backend parity: `cloud.region`).
 * Source is `kindeProperties.region`, never `useRegion()` (fails open to AU).
 */
export function setDatadogRegion(region: Region | null) {
  setGlobalDimension('region', region);
}

/**
 * Tag every RUM event and log line with the native host OS version/build
 * (PRODA-174) as **global** context, so auto-collected RUM errors, native
 * crashes and session replays — the events that DON'T flow through
 * `trackEventV2`'s per-event props — are filterable by host OS (e.g. Windows 10
 * `10.0.19045` vs 11 `10.0.22631`). Distinct from `desktop_version`, the Heidi
 * app semver. Set from `initDesktopOsInfo().then(...)` in `_app.tsx` once the
 * async Tauri IPC cache resolves (unknown at `initDatadog()` time). Tags BOTH
 * RUM and Logs — the RUM+Logs precedent is `update_channel` / `build_id`, since
 * the point is filtering auto-collected RUM errors/crashes by host OS (note
 * `desktop_version` in `_app.tsx` tags Logs only, so it's not the template
 * here). Desktop-only: the caller skips web/unknown hosts, so this is never
 * called with an empty value. On-prem-gated to match `initDatadog`; a no-op
 * there since RUM/Logs never initialize.
 */
export function setDatadogDesktopOsVersion(osVersion: string) {
  setGlobalDimension('desktop_os_version', osVersion);
}

/**
 * The complete set of host-machine global-context keys, and the
 * **second of two independent allowlists** guarding this payload (the first is
 * the named-field `HostSpecsResponse` struct in Rust).
 *
 * `setDatadogHostSpecs` iterates this list and sets ONLY these keys, ignoring
 * anything else present on the IPC payload. That matters because the existing
 * redaction does not protect this path: `pinoRedactedPaths` is a denylist, is
 * pino-only, is ignored by pino's browser build, and the Datadog transmit path
 * does not pass through it (open TODO ARCFE-679). Allowlisting on both sides
 * means a field a future `sysinfo` bump starts returning is dropped twice by
 * default, rather than sent by default.
 *
 * Never add hostname, kernel version, process-table data, volume name, mount
 * point, any path string, filesystem type, MAC address, machine GUID or a
 * BIOS/platform serial to this list. Full contract:
 * `docs/desktop/host-machine-specs-context-contract.md`.
 *
 * The second element of each pair is passed straight to `setGlobalDimension`,
 * so it must be a registered `GlobalDimensionKey` or this file does not
 * compile — the allowlist and the registry cannot drift apart silently.
 */
const HOST_SPEC_CONTEXT_KEYS = [
  // [field on the IPC payload, flat snake_case global-context key]
  ['hwCpuModel', 'hw_cpu_model'],
  ['hwCpuPhysicalCores', 'hw_cpu_physical_cores'],
  ['hwCpuLogicalCores', 'hw_cpu_logical_cores'],
  ['hwMemTotalBytes', 'hw_mem_total_bytes'],
  ['hwDiskTotalBytes', 'hw_disk_total_bytes'],
  ['hwDiskAvailableBytes', 'hw_disk_available_bytes'],
] as const;

export type HostSpecContextKey = (typeof HOST_SPEC_CONTEXT_KEYS)[number][1];

/**
 * Tag every RUM event and log line with the host machine's capability facts.
 * Contract: `docs/desktop/host-machine-specs-context-contract.md`.
 *
 * **`datadogLogs` is the load-bearing sink, not `datadogRum`.** Setting RUM
 * alone fails silently: `device_id` does not exist in the Rust base log
 * context, and the join works only because forwarded Rust log rows ARE browser
 * log rows and inherit this Logs global context. Drop the Logs write and every
 * dashboard still looks right while the one query this exists for returns
 * `device_id` with no hardware beside it. That is why all six rows name both
 * sinks in `GLOBAL_DIMENSIONS`, where a test can see it.
 *
 * Absent means absent: the six rows carry `onNullish: 'skip-empty'`, so a
 * field the machine did not answer produces NO key — never `0`, `""` or the
 * literal `"undefined"` that `setDatadogProperties`' `String(value)` loop
 * would emit. `0` and `false` ARE values and are written.
 *
 * Desktop-only; the caller skips web. On-prem-gated to match `initDatadog`; a
 * no-op there since RUM/Logs never initialize.
 */
export function setDatadogHostSpecs(specs: Record<string, unknown>): void {
  HOST_SPEC_CONTEXT_KEYS.forEach(([field, contextKey]) => {
    // The IPC payload is untyped by design — the Rust struct and
    // HOST_SPEC_CONTEXT_KEYS are the two allowlists, not this cast.
    setGlobalDimension(contextKey, specs?.[field] as GlobalDimensionValue);
  });
}

/**
 * Tag every RUM event and log line with the EHR launch profile this browser
 * session was launched under (`@ehr_profile_name`), so FE telemetry (including
 * the consent logs) can be split by launch profile the same way the backend
 * session-created log is. Unknown at `initDatadog()` time; set when a launch
 * profile becomes known. Only the Epic web launch (`useEpicLaunchComplete`)
 * tags it today; the Haiku FE stamp is a deferred follow-up. The value is
 * passed straight through (including null/undefined) so a later standalone
 * session in the same tab clears/overwrites a stale profile, mirroring
 * `setDatadogUpdateChannel`. On-prem-gated to match `initDatadog`; a no-op
 * there since RUM/Logs never initialize.
 */
export function setDatadogEhrProfileName(profileName: string | null | undefined) {
  setGlobalDimension('ehr_profile_name', profileName);
}

/**
 * The audio-ledger rollout cohort (`release-scribe-audio-ledger`) this page load
 * pinned. Ledger telemetry other than `LEDGER_MODE_PINNED` is cohort-blind, so
 * without it a partial ramp reads only as a diluted aggregate.
 *
 * RUM only (`@context.pipeline_mode`); the Logs half is bound inside
 * `@heidi/capture` on the `audio-recording` child logger, so it rides only the
 * records it can be joined to. `legacy` is stamped too — an unstamped session is
 * indistinguishable from an old bundle.
 *
 * Both variants are rows in `GLOBAL_DIMENSIONS`, not comments: `sinks: ['rum']`
 * and `platform: 'web'`. The web-only gate guards this exported setter against
 * a future caller; capture's coordinator already short-circuits earlier.
 * On-prem-gated to match `initDatadog`.
 */
export function setDatadogPipelineModeContext(mode: 'legacy' | 'ledger'): void {
  setGlobalDimension('pipeline_mode', mode);
}

/**
 * Which arm put the user in the sections cohort. Declared here rather than
 * beside the hook that computes it so the dependency runs `src/hooks` →
 * `src/utils` and never the reverse, and so the value space of the global RUM
 * property is closed at its own setter instead of accepting free text.
 */
export type SectionsSource =
  | 'markdown_native'
  | 'flag'
  | 'athena'
  | 'ambient_copilot'
  | 'off';

/**
 * Tag every RUM event and log line with the resolved sections cohort. OFF is
 * stamped too — an unstamped session is indistinguishable from an old bundle.
 */
export function setDatadogSectionsContext(
  enabled: boolean,
  source: SectionsSource
): void {
  setGlobalDimension('sections_enabled', enabled);
  setGlobalDimension('sections_source', source);
}

/**
 * Tag every RUM event and log line with the routed note/document editor
 * variant. `null` removes the property (pane unmount, PDF-only document) so
 * an unstamped record is distinguishable from `textarea`. Also writes the
 * process-local store that logger `enrich` reads, so log lines carry the
 * same dimension as auto-collected RUM errors.
 */
export function setDatadogEditorContext(variant: NoteEditorVariant | null): void {
  // TELEMETRY-GATE, kept HERE and not delegated to the primitive: the store
  // write below is a process-local side effect on both the null and non-null
  // branches, and it must not run under suppression either. The primitive
  // re-checks its own gate for the sink writes; that second check is
  // redundant, not a behaviour change.
  if (!isSaasTelemetryEnabled) return;
  setDatadogEditorVariantStore(variant);
  setGlobalDimension('editor_variant', variant);
}

/**
 * The backend-resolved editor version for an Athena onboarding session
 * (`template_editor_version` on the wire). Mirrors `EditorVersion` in
 * `src/hooks/editor-version/readStoredEditorVersion.ts`; declared HERE so the
 * dependency stays `src/hooks` → `src/utils` (see `SectionsSource` for the
 * same precedent) and the value space of the global property is closed at its
 * own setter instead of accepting free text.
 */
export type SectionalEditorVersion = 'html' | 'json' | 'markdown';

/**
 * Tag every RUM event and log line with the backend-resolved editor version
 * (AME-1002 §3). `null` removes the property — a non-Athena user carries no
 * sectional version, so an unstamped record is distinguishable from `html`.
 *
 * Fail-closed `html` (a null/unrecognized/error value on the wire) IS stamped
 * `html`: that is the editor the user gets, and "how many users are on the
 * legacy editor" is the query this dimension exists to answer. The cause of a
 * fail-closed fallthrough is a separate `logger` event, not this dimension.
 *
 * Dashboard: `sectional_editor_version:html|json|markdown` sliced against
 * `@usr.*` answers the CS migration question — "did the per-user flip land on
 * the client?" — that the backend document alone cannot confirm. Retires when
 * the AME-1002 migration completes and every Athena user carries a recognized
 * backend value.
 */
export function setDatadogSectionalEditorVersion(
  version: SectionalEditorVersion | null
): void {
  setGlobalDimension('sectional_editor_version', version);
}

export function setDatadogProperties(
  userId: string,
  properties: {
    ehrProvider?: EHRProvider;
    hasIntegration?: boolean;
    source?: string;
    teamId?: string;
    teamName?: string | null; // pass null when absent — the emit loop skips null but stringifies undefined
    workspaceId?: string | null;
    teamTier?: SubscriptionTier | string;
    userTier?: SubscriptionTier | string;
    userRefId?: string | null;
    idpSub?: string | null;
    // Auto-link-sessions visibility + stored state, same `@usr.*` boolean class
    // as `hasIntegration`. `autoLinkFlagOn` is the FE flag evaluation (can the
    // user SEE the toggle); `autoLinkIntent` is the stored
    // `session_settings.intends_to_auto_link_sessions`. Stamped HERE, inside the
    // same call as `setUser`, because `setUser` replaces the RUM user and drops
    // every prior `@usr.*` — so they must be re-applied on every identify.
    // Pass null when unresolved: the emit loop skips null but stringifies
    // undefined. So "is auto-link on for user X and what does the DB say?" is one
    // query (`@usr.autoLinkFlagOn` / `@usr.autoLinkIntent`) with zero repro.
    autoLinkFlagOn?: boolean | null;
    autoLinkIntent?: boolean | null;
  }
) {
  setUser({ userId });

  const { userRefId, idpSub, ...userProperties } = properties;

  // Tag every RUM event and log line with user_ref_id / idp_sub as TOP-LEVEL
  // global context properties (`@user_ref_id` / `@idp_sub`) — the same field
  // names and level the BE emits from the token-derived values (DEV-11834). A
  // single `@user_ref_id:<id>` or `@idp_sub:<sub>` query then joins FE and BE
  // rows. setUserProperty would nest them under `@usr.*`, which would not match
  // the BE attributes. idp_sub is the access token's `sub` claim — read from
  // the same token the BE verifies, so the values are identical by
  // construction. Set on auth resolution (the user is unknown when
  // initDatadog() runs); the next login overwrites it, mirroring setUser.
  // Both rows carry an explicit `ungatedOnPremBecause` — this path has no
  // isOnPrem guard today and gating it here would change the on-prem call
  // sequence for no product benefit. The truthiness `if`s stay caller-side.
  if (userRefId) {
    setGlobalDimension('user_ref_id', userRefId);
  }
  if (idpSub) {
    setGlobalDimension('idp_sub', idpSub);
  }

  Object.entries(userProperties).forEach(([key, value]) => {
    if (value !== null) {
      setUserProperty({ id: key, value: String(value) });
    }
  });
}
